import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import AppLayout from '../../component/appLayout';
import {
  HeaderBlock,
  Heading,
  Icon,
  Title,
  DateSlider,
  SliderBox,
  DateTitle,
  TimeTitle,
  TimeTrackingDiv,
  MainContent,
  TopicData,
  LeftContent,
  RightContent,
  TopicDescription,
  TopicTitle,
  TimeStop,
  TrackingContent,
  MiddleLine,
  RightSideIcon,
  Header,
  InvoiceLocked,
  IconSection,
  TImeSection,
  Dot,
  Empty
} from './styles';
import {
  archiveTimeLog,
  getActiveTime,
  getTimeLogs,
  loadEditTimeLog,
  startTracker,
  stopTracker,
  updateTimeLogDetails
} from '../../services/timeTrackingServices';
import { floatToHHMM, getDateObjecttoDDMMM, getDateString, isEmpty } from '../../helpers/common';
import AddNewTimeModal from '../../component/modals/addNewTimeModal';
import TimeLoading from '../../component/loading/timeTrackingLoading';
import { LogDataInterface, TimeTrackingDataInterface } from '../../interfaces/TimeTrackingInterface';
import Button from '../../component/button';
import Modal from '../../component/modals/modal';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { nanoid } from 'nanoid';
import ConfirmationModal from '../../component/modals/confirmationModal';
import { captureException } from '../../services/logService';

import { useHistory } from 'react-router-dom';
import { rootStore } from '../../mobx/rootStore';
import { useMobileDevice } from '../../global/useMobile';
import ResponsiveHeader from '../../component/responsiveHeader';
import BottomSubNavigation from '../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import {
  BUTTON_TYPE,
  CustomerDetailMoreMenu,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  MENU_OPTIONS
} from '../../global/constants';
import IconButton from '../../component/button/iconButton';
import { observer } from 'mobx-react-lite';
import { Dropdown, DropdownItem } from '../../component/dropdown';
import DeleteModal from '../../component/modals/deleteModal';
import { useTabletDevice } from '../../global/useTablet';
import EmptyStateV2 from '../../component/emptyState';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { ROUTES } from '../../routes/urls';

const TimeTracking: React.FC = () => {
  // Mobx Variables
  const { workspaceStore, timeTrackingStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id || '';
  const {
    activeTimeLogs,
    timeTackingData,
    setTimeTackingData,
    currentTimeTracking,
    setCurrentTimeTracking,
    clearCurrentTimeTracking,
    updateTaskInStore,
    setTimeEntryInput,
    clearTimeEntryInput,
    loading
  } = timeTrackingStore;
  // Other variables
  const curr = new Date();
  const first = new Date(curr);
  const last = new Date(curr);
  if (curr.getDay() === 0) {
    first.setDate(curr.getDate() - 6); // Set to Monday of the previous week
    last.setDate(curr.getDate()); // Set to today (Sunday)
  } else {
    // Calculate the first day of the week (Monday)
    first.setDate(curr.getDate() - curr.getDay() + 1);
    // Calculate the last day of the week (Sunday)
    last.setDate(curr.getDate() - curr.getDay() + 7);
  }
  const firstDay = first.toUTCString();
  const lastDay = last.toUTCString();
  const history = useHistory();
  const isMobile = useMobileDevice();
  const isTablet = useTabletDevice();

  const responsiveDevice = isMobile || isTablet;
  // States
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [loadings, setLoadings] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState<LogDataInterface>();
  const [alertModelOpen, setAlertModalOpen] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // handle icon click
  const handleIconClick = useCallback(() => {
    history.push(ROUTES.MY_TIMELOG);
  }, [history]);

  // load initial data
  const loadData = useCallback(
    async (startDate: string, endDate: string, activeDate: string | undefined) => {
      if (!isEmpty(workspace_id) && startDate && endDate) {
        setLoadings(true);
        await getTimeLogs(workspace_id, startDate, endDate, activeDate);
        setLoadings(false);
      }
    },
    [workspace_id]
  );

  useEffect(() => {
    const activeDate = getDateString(currentTimeTracking?.date ? new Date(currentTimeTracking?.date) : new Date());
    loadData(startDate, endDate, activeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace_id]);

  useEffect(() => {
    return () => {
      setTimeTackingData([]);
      clearCurrentTimeTracking();
    };
  }, [setTimeTackingData, clearCurrentTimeTracking]);

  // Sets the current time tracking item if it's active.
  const setClickTimeTab = useCallback(
    (item: TimeTrackingDataInterface) => {
      if (!item?.active) return;
      setCurrentTimeTracking(item);
    },
    [setCurrentTimeTracking]
  );

  // for handle left side button click
  const onClickLeft = useCallback(() => {
    let currentIndex = currentTimeTracking?.index;
    if (currentIndex === 0) {
      clearCurrentTimeTracking();
      const rightDate = dayjs(new Date(currentTimeTracking?.date)).add(-1, 'day').toLocaleString();
      const leftDate = dayjs(new Date(currentTimeTracking?.date)).add(-7, 'day').toLocaleString();
      setStartDate(leftDate);
      setEndDate(rightDate);
      loadData(leftDate, rightDate, getDateString(rightDate));
    }
    let newObj = timeTackingData[currentIndex - 1];
    if (!newObj?.active) return;
    setCurrentTimeTracking(newObj);
  }, [timeTackingData, currentTimeTracking, loadData, setCurrentTimeTracking, clearCurrentTimeTracking]);

  // for handle right side button click
  const onClickRight = useCallback(() => {
    let currentIndex = currentTimeTracking?.index;
    if (currentIndex === 6) {
      clearCurrentTimeTracking();
      const leftDate = dayjs(new Date(currentTimeTracking?.date)).add(1, 'day').toLocaleString();
      const rightDate = dayjs(new Date(currentTimeTracking?.date)).add(8, 'day').toLocaleString();

      setStartDate(leftDate);
      setEndDate(rightDate);
      loadData(leftDate, rightDate, getDateString(leftDate));
    }
    let newObj = timeTackingData[currentIndex + 1];
    if (!newObj?.active) return;
    setCurrentTimeTracking(timeTackingData[currentIndex + 1]);
  }, [timeTackingData, currentTimeTracking, loadData, setCurrentTimeTracking, clearCurrentTimeTracking]);

  // function for start time
  const startTime = useCallback(
    async (item: LogDataInterface) => {
      if (!loading) {
        if (activeTimeLogs?.length > 0) {
          setSelectedTracker(item);
          setAlertModalOpen(true);
          return;
        }

        const payloadUpdate = {
          TimerStartedAt: new Date().toISOString()
        };
        const result = await updateTimeLogDetails(item?.id, payloadUpdate);
        if (result) {
          startTracker(item?.taskId);
          updateTaskInStore({
            ...item,
            timer_started_at: result?.data?.TimerStartedAt
          });
          rootStore.alertNotificationStore.setSuccessMessage('Time Started!');
        }
        await getActiveTime();
      }
    },
    [loading, activeTimeLogs?.length, updateTaskInStore]
  );

  // for manage stop timer click
  const onClickStop = useCallback(
    async (item: LogDataInterface, startingTime: string) => {
      if (!loading) {
        const date1 = new Date(startingTime).getTime();
        const date2 = new Date().getTime();
        const timeDifference = date2 - date1; // Difference in milliseconds
        const hoursDifference = timeDifference / (1000 * 60 * 60);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

        const historyHoursSum = item?.history?.reduce((total, historyItem) => {
          return total + (historyItem.Hours || 0);
        }, 0);

        const payloadUpdate = {
          Hours: Number(Number(historyHoursSum || 0) + hoursDifference),
          TimerStartedAtUnset: true,
          History: [
            ...(item?.history || []),
            {
              _id: nanoid(),
              CreatedBy: userDetails?.id,
              CreatedTime: new Date().toISOString(),
              UpdatedBy: userDetails?.id,
              UpdatedTime: new Date().toISOString(),
              TimerStartedAt: startingTime,
              TimerEndedAt: new Date().toISOString(),
              Hours: hoursDifference
            }
          ]
        };
        const result = await updateTimeLogDetails(item?.id, payloadUpdate);

        if (result) {
          stopTracker();
          updateTaskInStore({
            ...item,
            timer_started_at: result?.data?.TimerStartedAt,
            hours: result?.data?.Hours
          });
          rootStore.alertNotificationStore.setSuccessMessage('Time Stopped!');
        }
        await getActiveTime();
      }
    },
    [loading, updateTaskInStore]
  );

  // for open add modal
  const onOpenAddModal = useCallback(() => {
    setOpen(true);
  }, []);

  // close alert model
  const closeAlertModal = useCallback(() => {
    setAlertModalOpen(false);
  }, []);

  // Loads the edit time log and opens the add time log modal.
  const onClickLogTime = useCallback(async () => {
    await loadEditTimeLog();
    onOpenAddModal();
  }, [onOpenAddModal]);

  // close add model
  const closeAddModel = useCallback(() => {
    setOpen(false);
    clearTimeEntryInput();
    loadData(startDate, endDate, currentTimeTracking?.formated_date);
  }, [clearTimeEntryInput, loadData, startDate, endDate, currentTimeTracking?.formated_date]);

  // edit time entry
  const onClickEdit = useCallback(
    async (item: LogDataInterface) => {
      setTimeEntryInput({ ...toJS(item), hours: item.hours.toString() });
      await loadEditTimeLog();
      onOpenAddModal();
    },
    [onOpenAddModal, setTimeEntryInput]
  );

  // manage stop and start time
  const onClickStopAndStartTime = useCallback(async () => {
    try {
      setAlertLoading(true);
      if (!loading) {
        const date1 = new Date(activeTimeLogs[0]?.TimerStartedAt).getTime();
        const date2 = new Date().getTime();
        const timeDifference = date2 - date1; // Difference in milliseconds
        const hoursDifference = timeDifference / (1000 * 60 * 60);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const itemId = activeTimeLogs[0]?.['_id'];
        const historyUpdated = activeTimeLogs[0]?.History?.map((item) => {
          const { __typename, ...other } = item;
          return other;
        });

        const historyHoursSum = activeTimeLogs[0]?.History?.reduce((total, historyItem) => {
          return total + (historyItem.Hours || 0);
        }, 0);

        const payloadPrevious = {
          Hours: Number(Number(historyHoursSum || 0) + hoursDifference),
          TimerStartedAtUnset: true,
          History: [
            ...(historyUpdated || []),
            {
              _id: nanoid(),
              CreatedBy: userDetails?.id,
              CreatedTime: new Date().toISOString(),
              UpdatedBy: userDetails?.id,
              UpdatedTime: new Date().toISOString(),
              TimerStartedAt: activeTimeLogs[0]?.TimerStartedAt,
              TimerEndedAt: new Date().toISOString(),
              Hours: hoursDifference
            }
          ]
        };
        await updateTimeLogDetails(itemId, payloadPrevious);

        const payload = {
          TimerStartedAt: new Date().toISOString()
        };
        const result = await updateTimeLogDetails(selectedTracker?.id || '', payload);
        if (result) {
          startTracker(selectedTracker?.taskId);
          if (selectedTracker) {
            updateTaskInStore({
              ...selectedTracker,
              timer_started_at: result?.data?.TimerStartedAt
            });
          }
          setAlertModalOpen(false);
          rootStore.alertNotificationStore.setSuccessMessage('Time Started!');
          await getActiveTime();
          await getTimeLogs(workspace_id, startDate, endDate, getDateString(new Date()));
        }
      }
    } catch (error) {
      captureException(error);
      setAlertLoading(false);
    } finally {
      setAlertLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedTracker, activeTimeLogs, workspace_id]);

  const renderTimeLogSection = useCallback((element: LogDataInterface) => {
    // Calculate start and end time
    const startTime = dayjs(element.trackingStartTime || element.createdOn);
    const endTime = startTime.add(element.hours, 'hour');
    const timeRange = `${startTime.format('hh:mm A')} - ${
      element.timer_started_at ? 'Running' : endTime.format('hh:mm A')
    }`;
    const duration = floatToHHMM(element.hours);

    return (
      <TImeSection>
        {/* at end time if tracker is not stopped then show Runing */}
        <TimeTitle>{timeRange}</TimeTitle>
        <Dot />
        <TimeTitle>{duration}</TimeTitle>
      </TImeSection>
    );
  }, []);

  // handle delete click
  const handleDeleteClick = useCallback((item: LogDataInterface) => {
    setSelectedTracker(item);
    setIsDeleteModelOpen(true);
  }, []);

  // handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedTracker?.id) return;

    const result = await archiveTimeLog(selectedTracker.id);
    if (result) {
      setIsDeleteModelOpen(false);
      setSelectedTracker(undefined);
      // Reload the time logs after successful deletion
      loadData(startDate, endDate, currentTimeTracking?.formated_date);
    }
  }, [selectedTracker, startDate, endDate, currentTimeTracking?.formated_date, loadData]);

  // handle more menu click
  const handleMoreMenuClick = useCallback(
    (key: number | string, item: LogDataInterface) => {
      if (key === MENU_OPTIONS.EDIT) {
        onClickEdit(item);
      } else if (key === MENU_OPTIONS.DELETE) {
        handleDeleteClick(item);
      }
    },
    [handleDeleteClick, onClickEdit]
  );

  // render more menu
  const renderMoreMenu = useCallback(
    (item: LogDataInterface) => {
      return CustomerDetailMoreMenu.map((menuItem) => (
        <DropdownItem
          key={menuItem.key}
          label={menuItem.label}
          tone={menuItem.key === MENU_OPTIONS.DELETE ? 'critical' : 'primary'}
          onClick={() => handleMoreMenuClick(menuItem.key, item)}
        />
      ));
    },
    [handleMoreMenuClick]
  );

  // handle close delete model
  const closeDeleteModel = useCallback(() => {
    setIsDeleteModelOpen(false);
  }, []);

  const renderDateSlider = useCallback(() => {
    return (
      <DateSlider>
        <IconButton
          iconName='new-back-arrow-icon'
          size={ICON_BUTTON_SIZE.LARGE}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={onClickLeft}
          className='left-arrow-icon time-tracking-arrow-icon'
          isDisabled={loading}
        />
        <MiddleLine>
          {timeTackingData?.map((item) => {
            return (
              <SliderBox
                role='button'
                key={item?.day}
                isDisabled={!item?.active}
                isCurrent={item?.formated_date === currentTimeTracking?.formated_date}
                onClick={() => setClickTimeTab(item)}>
                <DateTitle isDisabled={!item?.active}>{item?.day}</DateTitle>
                <TimeTitle isDisabled={!item?.active}>{floatToHHMM(item?.hours)}</TimeTitle>
              </SliderBox>
            );
          })}
        </MiddleLine>
        <IconButton
          iconName='updated-right-arrow-icon'
          size={ICON_BUTTON_SIZE.LARGE}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={onClickRight}
          className='right-arrow-icon time-tracking-arrow-icon'
          isDisabled={loading}
        />
      </DateSlider>
    );
  }, [currentTimeTracking?.formated_date, loading, onClickLeft, onClickRight, setClickTimeTab, timeTackingData]);

  // render current time tracking data
  const renderCurrentTimeTrackingLogData = useMemo(() => {
    if (currentTimeTracking?.logData?.length === 0 && (loading || loadings)) {
      return <TimeLoading />;
    } else {
      return (
        <TrackingContent isEmptyState={currentTimeTracking?.logData?.length === 0 && !loading}>
          {renderDateSlider()}
          {currentTimeTracking?.logData?.length === 0 && !loading && (
            <Empty className='desktop-time-tracking-content-empty'>
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'You have no active timelogs currently'}
                description={'Click the button below to get started'}
                imageUrl={'time_tracking_empty'}
                buttonTitle={'Log Time'}
                onClick={onClickLogTime}
                isAlignedCenter={true}
              />
            </Empty>
          )}
          {currentTimeTracking?.logData?.length > 0 && (
            <MainContent>
              {currentTimeTracking?.logData?.map((element) => {
                return (
                  <TopicData key={element?.id}>
                    <LeftContent>
                      <TopicTitle>{element?.projectName}</TopicTitle>
                      {responsiveDevice && renderTimeLogSection(element)}
                      <TopicDescription>
                        {element?.taskGroupName} {element?.taskName} {element?.notes}
                      </TopicDescription>
                    </LeftContent>
                    <RightContent>
                      {!responsiveDevice && renderTimeLogSection(element)}
                      <IconSection>
                        {!element?.isInvoiced && (
                          <>
                            {element?.timer_started_at ? (
                              <TimeStop role='button' onClick={() => onClickStop(element, element?.timer_started_at)}>
                                <SVGIcon
                                  name='time-start-icon'
                                  width={ICON_SIZE.XXL}
                                  height={ICON_SIZE.XXL}
                                  viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                                  fill='var(--text-muted)'
                                />
                              </TimeStop>
                            ) : (
                              <TimeStop role='button' onClick={() => startTime(element)}>
                                <SVGIcon
                                  name='time-stop-icon'
                                  width={ICON_SIZE.XXL}
                                  height={ICON_SIZE.XXL}
                                  viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                                  className='time-stop-icon'
                                />
                              </TimeStop>
                            )}
                          </>
                        )}
                        {element?.isInvoiced ? (
                          <InvoiceLocked>
                            <IconButton
                              iconName='lock-icon'
                              size={ICON_BUTTON_SIZE.SMALL}
                              variant={ICON_BUTTON_TYPE.TERTIARY}
                              className='lock-icon'
                              iconColorType='primary'
                            />
                          </InvoiceLocked>
                        ) : (
                          <Dropdown
                            activeClassName='active-dropdown'
                            modalTitle='Manage Time Log'
                            content={
                              <IconButton
                                iconName='new-three-dots-vertical-icon'
                                size={ICON_BUTTON_SIZE.SMALL}
                                variant={ICON_BUTTON_TYPE.TERTIARY}
                              />
                            }
                            position='right'
                            trigger='click'>
                            {renderMoreMenu(element)}
                          </Dropdown>
                        )}
                      </IconSection>
                    </RightContent>
                  </TopicData>
                );
              })}
            </MainContent>
          )}
        </TrackingContent>
      );
    }
  }, [
    currentTimeTracking?.logData,
    loading,
    loadings,
    onClickLogTime,
    onClickStop,
    renderDateSlider,
    renderMoreMenu,
    renderTimeLogSection,
    responsiveDevice,
    startTime
  ]);

  // in mobile view first 3 letters will be shown for current day in header
  const currentDay = isMobile ? currentTimeTracking?.day.slice(0, 3) : currentTimeTracking?.day;

  return (
    <>
      <AppLayout>
        {!isMobile && (
          <HeaderBlock>
            <Header>
              <Heading>
                <Icon className='timeHeader'>
                  <SVGIcon
                    name='navbar-TimeTracking-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--brand-primary)'
                  />
                </Icon>
                {currentTimeTracking?.date && (
                  <Title>
                    {currentDay}, {getDateObjecttoDDMMM(currentTimeTracking?.date)}
                  </Title>
                )}
              </Heading>
            </Header>
            <RightSideIcon>
              <IconButton
                iconName='my-timelog-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={handleIconClick}
              />
              <Button
                title='Log Time'
                variant={BUTTON_TYPE.PRIMARY}
                onClick={onClickLogTime}
                iconName='plus-icon'
                iconPosition='start'
                buttonType='button'
                size='small'
              />
            </RightSideIcon>
          </HeaderBlock>
        )}
        <TimeTrackingDiv className='desktop-time-tracking-content'>
          {isMobile && (
            <ResponsiveHeader
              title={`${
                !currentDay
                  ? new Date().toLocaleDateString('en-US', {
                      weekday: 'short'
                    })
                  : currentDay
              }, ${getDateObjecttoDDMMM(currentTimeTracking?.date || new Date().toISOString())}`}
            />
          )}
          {renderCurrentTimeTrackingLogData}
        </TimeTrackingDiv>
      </AppLayout>
      <Modal open={open} onClose={closeAddModel} maxWidth='520px'>
        <AddNewTimeModal onCloseModal={closeAddModel} loadData={() => loadData} />
      </Modal>
      <Modal open={alertModelOpen} onClose={closeAlertModal} maxWidth={'334px'}>
        <ConfirmationModal
          loading={alertLoading}
          onOk={onClickStopAndStartTime}
          onClose={closeAlertModal}
          modalTitle='Another timer is running.'
          description='Do you want to stop and start the timer on this task?'
        />
      </Modal>
      <Modal open={isDeleteModelOpen} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          onOk={handleDeleteConfirm}
          modalTitle='Delete time log?'
          description='Are you sure you want to delete this time log?'
          loading={loading}
        />
      </Modal>
      <BottomSubNavigation
        rightElement={
          <>
            <BottomNavigationItem iconName='my-timelog-icon' onClick={handleIconClick} />
            <BottomNavigationItem iconName='plus-icon' onClick={onClickLogTime} />
          </>
        }
      />
    </>
  );
};

export default observer(TimeTracking);
